import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { InjectionToken, NgModule } from '@angular/core';
import { NgxsModule } from '@ngxs/store';
import {
  ActionRequiredState,
  AppEnvironmentState,
  BreadcrumbV3Component,
  ButtonV3Component,
  FeatureFlagConfigurationState,
  GeaMissingTranslationHandler,
  GeaTranslateHttpLoader,
  HeaderConfig,
  HeaderModule,
  HierarchyTableV3Component,
  IconV3Component,
  InputV3Component,
  ModalV2Module,
  PageContentLayoutV3Component,
  PageFooterComponent,
  PageHeaderV3Component,
  PageLayoutV3Component,
  PermissionsState,
  ProgressSpinnerComponent,
  RadioGroupV3Component,
  SelectV2Module,
  SidebarV3Component,
  SnackbarV3Component,
  SnackbarV3Service,
  UserState,
} from '@gea/digital-ui-lib';
import { MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '../environments/models/environment.model';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormlyModule } from '@ngx-formly/core';
import { GeaUiInputV3TypeComponent } from 'src/formly/geaui-input-v3.type';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GeaUiSelectV2TypeComponent } from 'src/formly/geaui-select-v2.type';
import { GeaUiCheckboxV3TypeComponent } from 'src/formly/geaui-checkbox-v3.type';
import { PanelWrapperComponent } from 'src/formly/formlyPanelWrapper';
import { InputWrapperComponent } from 'src/formly/formlyInputFieldWrapper';
import { GeaUiTableV3TypeComponent } from 'src/formly/geaui-table-v3.type';
import { FullWidthWrapperComponent } from 'src/formly/formlyFullWidthWrapper';
import { DynamicalTabListComponent } from 'src/formly/DynamicalTabList.type';
import { ImageFieldComponent } from 'src/formly/geaui-image-v3.type';
import { PanelWrapperComponentV2 } from 'src/formly/formlyPanelWrapper-v2';
import { FormlyBannerComponent } from 'src/formly/banner';
import { FormlySkeletonLoaderComponent } from 'src/formly/formlySkeletonLoader';
import { brsInterceptor } from './shared/interceptors/brs.interceptor';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { DashboardCardComponent } from './shared/components/dashboard-card/dashboard-card.component';
import { DashboardProductsCardComponent } from './shared/components/dashboard-products-card/dashboard-products-card.component';
import { NewsBannerComponent } from './shared/components/news-banner/news-banner.component';
import { TabGroupComponent } from './shared/components/tab-group/tab-group.component';
// PrimeNG
import { CarouselModule } from 'primeng/carousel';
import { DialogModule } from 'primeng/dialog';
import { Menu } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Step, StepList, Stepper } from 'primeng/stepper';
import { TreeModule } from 'primeng/tree';
import { ButtonModule } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { FormlyDividerComponent } from 'src/formly/formlyFieldDivider';
import { FormlySpacerComponent } from 'src/formly/formlyFieldSpacer';
import { DataMaintenanceComponent } from './pages/data-maintenance/data-maintenance.component';
import { DataMaintenanceContentComponent } from './pages/data-maintenance/content/data-maintenance-content.component';
import { GeaUiRadioGroupV3TypeComponent } from '../formly/geaui-radio-group-v3.type';
import { ProductConfigurationState } from './state/product-configuration';

export const HEADER_CONFIG = new InjectionToken<HeaderConfig>('HeaderConfig');

// <CHANGE ME> - Change the path to your files
export function HttpLoaderFactory(http: HttpClient, environment: EnvironmentConfiguration) {
  return new GeaTranslateHttpLoader(http, environment.localizationStorageURL, ['i18n/1/common/']);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    NgxsModule.forRoot([UserState, ActionRequiredState, AppEnvironmentState, PermissionsState, FeatureFlagConfigurationState, ProductConfigurationState], {
      compatibility: {
        strictContentSecurityPolicy: true,
      },
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    HeaderModule.forRoot(HEADER_CONFIG),
    MsalModule,
    SelectV2Module,
    DialogModule,
    ModalV2Module,
    IconV3Component,
    Menu,
    ButtonModule,
    TranslateModule.forRoot({
      extend: true,
      defaultLanguage: 'en-US',
      useDefaultLang: false,
      loader: {
        deps: [HttpClient, ENVIRONMENT_CONFIG],
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: GeaMissingTranslationHandler,
      },
    }),
    ProgressSpinnerComponent,
    ReactiveFormsModule,
    SelectV2Module,
    InputV3Component,
    RadioGroupV3Component,
    BreadcrumbV3Component,
    FormlyModule.forRoot({
      types: [
        {
          name: 'input',
          component: GeaUiInputV3TypeComponent,
          wrappers: ['input'],
        },
        {
          name: 'number',
          component: GeaUiInputV3TypeComponent,
          wrappers: ['input'],
        },
        {
          name: 'select',
          component: GeaUiSelectV2TypeComponent,
          wrappers: ['input'],
        },
        {
          name: 'checkbox',
          component: GeaUiCheckboxV3TypeComponent,
        },
        {
          name: 'radiogroup',
          component: GeaUiRadioGroupV3TypeComponent,
        },
        {
          name: 'table',
          component: GeaUiTableV3TypeComponent,
          wrappers: ['fullWidth'],
        },
        {
          name: 'dynamicalTabList',
          component: DynamicalTabListComponent,
        },
        { name: 'image', component: ImageFieldComponent },
        {
          name: 'banner',
          component: FormlyBannerComponent,
        },
        { name: 'spacer', component: FormlySpacerComponent },
        { name: 'divider', component: FormlyDividerComponent },
      ],
      wrappers: [
        {
          name: 'panel',
          component: PanelWrapperComponent,
        },
        {
          name: 'panelV2',
          component: PanelWrapperComponentV2,
        },
        {
          name: 'input',
          component: InputWrapperComponent,
        },
        {
          name: 'fullWidth',
          component: FullWidthWrapperComponent,
        },
      ],
    }),
    ButtonV3Component,
    HierarchyTableV3Component,
    SnackbarV3Component,
    CarouselModule,
    FormsModule,
    PdfViewerModule,
    DashboardCardComponent,
    TabGroupComponent,
    DashboardProductsCardComponent,
    NewsBannerComponent,
    TreeModule,
    SelectButtonModule,
    Stepper,
    Step,
    StepList,
    PageHeaderV3Component,
    Skeleton,
    PageFooterComponent,
    FormlySkeletonLoaderComponent,
    PageContentLayoutV3Component,
    DataMaintenanceComponent,
    DataMaintenanceContentComponent,
    PageLayoutV3Component,
    SidebarV3Component,
  ],
  exports: [HierarchyTableV3Component, ButtonV3Component, IconV3Component],
  providers: [
    {
      provide: 'baseUrl',
      useFactory: (env: EnvironmentConfiguration) => env.portal.baseURL ?? env.baseURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'subscriptionKey',
      useValue: '',
    },
    {
      provide: 'storageURL',
      useFactory: (env: EnvironmentConfiguration) => env.localizationStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    {
      provide: 'iconPath',
      useFactory: (env: EnvironmentConfiguration) => env.iconStorageURL,
      deps: [ENVIRONMENT_CONFIG],
    },
    MsalService,
    SnackbarV3Service,
    provideHttpClient(withInterceptors([brsInterceptor])),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
